import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import toast from 'react-hot-toast';

const SectionTable = ({ title, section, onAccountClick }) => (
  <div className="bg-white rounded-xl shadow overflow-hidden mb-4">
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <span className="text-sm font-semibold text-gray-800">{section.total.toFixed(2)}</span>
    </div>
    {section.groups.length === 0 ? (
      <div className="text-center py-6 text-gray-400 text-sm">No activity</div>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {section.groups.map(group => (
            <React.Fragment key={group.classification}>
              <tr className="bg-gray-50">
                <td colSpan={2} className="px-4 py-1.5 text-xs font-semibold text-gray-500">{group.classification}</td>
              </tr>
              {group.accounts.map(a => (
                <tr key={a.id} onClick={() => onAccountClick(a.id)} className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-1.5 text-gray-700">{a.account_code} — {a.account_name}</td>
                  <td className="px-4 py-1.5 text-right text-gray-800">{a.balance.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-100">
                <td className="px-4 py-1.5 text-right text-xs text-gray-500">Subtotal</td>
                <td className="px-4 py-1.5 text-right font-medium text-gray-800">{group.subtotal.toFixed(2)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const RevenueExpenditure = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  const runReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      const response = await accountsApi.get('/financial-statements/revenue-expenditure', { params });
      setData(response.data.revenue_expenditure);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onAccountClick = (accountId) => {
    const params = new URLSearchParams({ account_id: accountId });
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    navigate(`/ledgers?${params.toString()}`);
  };

  return (
    <Layout title="Statement of Revenue and Expenditure">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
            <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Cost Centers</option>
              {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : !data ? null : (
        <>
          {data.warnings?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4 space-y-1">
              {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}

          <SectionTable title="Revenue" section={data.sections.revenue} onAccountClick={onAccountClick} />
          <SectionTable title="Expenditure" section={data.sections.expenditure} onAccountClick={onAccountClick} />

          <div className={`rounded-xl p-4 flex items-center justify-between ${data.surplus_deficit >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <span className={`font-semibold ${data.surplus_deficit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {data.surplus_deficit >= 0 ? 'Surplus' : 'Deficit'}
            </span>
            <span className={`font-semibold ${data.surplus_deficit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {data.surplus_deficit.toFixed(2)}
            </span>
          </div>
        </>
      )}
    </Layout>
  );
};

export default RevenueExpenditure;
